import React, { FC, useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { removeBackground } from "@imgly/background-removal";
import { TextSignature} from "../textSignature";
import { convertToBlackSignature, dataURLtoFile } from "../../../../helpers/addSignature";

export type SignatureType = 'draw' | 'upload' | 'text';

export interface SignatureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (file: File) => Promise<void> | void;
  isLoading?: boolean;
  error?: string | null;
}

export const SignatureModal: FC<SignatureModalProps> = ({isOpen, onClose, onSave, isLoading = false, error = null}) => {
  const [activeTab, setActiveTab] = useState<SignatureType>('draw');
  const sigCanvas = useRef<SignatureCanvas>(null);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [textSignature, setTextSignature] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [progress, setProgress] = useState<number>(0);
  const [processedImage, setProcessedImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [signCanvas,setSignCanvas] = useState<any>(sigCanvas)
  const handleSave = async () => {
    setLocalError(null);

    try {
      let signatureFile: File | null = null;

      if (activeTab === 'draw' && sigCanvas.current) {
        if (sigCanvas.current.isEmpty()) {
          throw new Error('Please draw your signature first');
        }
        const dataUrl = sigCanvas.current.toDataURL('image/png');
        setSignCanvas(sigCanvas.current)
        signatureFile = dataURLtoFile(dataUrl, `signature_${Date.now()}.png`);

      }
      else if (activeTab === 'upload' && processedImage) {
        const blob = await fetch(processedImage).then(r => r.blob());
        const signatureFileName = `signature_${Date.now()}.png`;
        signatureFile = new File([blob], signatureFileName, { type: 'image/png' });
      }

      else if (activeTab === 'text' && textSignature) {
        signatureFile = dataURLtoFile(textSignature, `signature_${Date.now()}.png`);
      } else {
        throw new Error('No signature provided');
      }

      await onSave(signatureFile);
      onClose();
    } catch (err) {
      setLocalError(err instanceof Error ? err.message : 'Failed to process signature');
      console.error('Signature processing error:', err);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setLocalError('Only JPG, JPEG, and PNG formats are allowed.');
      return;
    }

    setLocalError(null);
    setProcessedImage(null);
    setIsProcessingImage(true);
    setProgress(0);

    const reader = new FileReader();

    reader.onload = async (event) => {
      const imageDataUrl = event.target?.result as string;
      setUploadedImage(imageDataUrl);

      // Simulate progress while processing
      let simulatedProgress = 0;
      const progressInterval = setInterval(() => {
        simulatedProgress += 10;
        setProgress((prev) => (prev < 90 ? simulatedProgress : prev));
      }, 200);

      try {
        const blob = await fetch(imageDataUrl).then((r) => r.blob());

        const processedBlob = await removeBackground(blob, {
          output: {
            format: 'image/png',
            quality: 0.8,
          },
          debug: true,
          proxyToWorker: true,
        });

        clearInterval(progressInterval);
        setProgress(100);

        const processedImageUrl = URL.createObjectURL(processedBlob);
        setProcessedImage(processedImageUrl);
      } catch (err) {
        console.error('Error processing uploaded image:', err);
        setLocalError('Failed to remove background from image.');
        clearInterval(progressInterval);
      } finally {
        setTimeout(() => {
          setIsProcessingImage(false);
          setProgress(0);
        }, 500);
      }
    };

    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (!isOpen) {
      setUploadedImage(null);
      setProcessedImage(null);
      setTextSignature(null);
      setLocalError(null);
      setProgress(0);
      setIsProcessingImage(false);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

    } else {
      setActiveTab('draw');
    }
  }, [isOpen]);


  if (!isOpen) return null;

  return (
      <div className="modal" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered modal-md">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Add Signature</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>
            <div className="modal-body">
              <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                  <button
                      type="button"
                      className={`nav-link ${activeTab === 'draw' ? 'active' : ''}`}
                      onClick={() => setActiveTab('draw')}
                  >
                    Draw
                  </button>
                </li>
                <li className="nav-item">
                  <button
                      type="button"
                      className={`nav-link ${activeTab === 'upload' ? 'active' : ''}`}
                      onClick={() => setActiveTab('upload')}
                  >
                    Upload
                  </button>
                </li>
                {/*<li className="nav-item">*/}
                {/*  <button*/}
                {/*      type="button"*/}
                {/*      className={`nav-link ${activeTab === 'text' ? 'active' : ''}`}*/}
                {/*      onClick={() => setActiveTab('text')}*/}
                {/*  >*/}
                {/*    Text*/}
                {/*  </button>*/}
                {/*</li>*/}
              </ul>

              {activeTab === 'draw' ? (
                  <div className="signature-canvas-container">
                    <div className="signature-canvas-control">
                      <SignatureCanvas
                          ref={sigCanvas}
                          penColor="black"
                          canvasProps={{ className:'w-100 h-100'}}
                      />
                    </div>
                    <button type="button" className="btn text-danger p-0"
                        onClick={() => sigCanvas.current?.clear()}>
                      Clear
                    </button>
                  </div>
              ) : activeTab === 'upload' ? (
                  <div className="upload-section">
                    <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        onChange={handleImageUpload}
                        className="form-control"
                        ref={fileInputRef}
                    />

                    {isProcessingImage ? (
                        <div className="mt-3">
                          <div className="progress" style={{ height: '20px' }}>
                            <div
                                className="progress-bar progress-bar-striped progress-bar-animated"
                                role="progressbar"
                                style={{ width: `${progress}%` }}
                            >
                              {progress}%
                            </div>
                          </div>
                        </div>
                    ) : processedImage ? (
                        <div className="mt-3 signature-canvas-control d-flex align-items-center justify-content-center">
                          <img
                              src={processedImage}
                              alt="Processed upload"
                              className="img-fluid"
                              style={{maxHeight: '200px'}}
                          />
                        </div>
                    ) : null}

                    <p className="alert alert-warning px-2 py-1 small mt-3 m-0">
                      Please upload a clear image of your signature only (not a full-page document), on a white
                      background,
                      with minimal size and no shadows or borders. Accepted formats: JPG, JPEG or PNG (max 1MB).
                      This signature will be securely stored for future use.
                    </p>
                  </div>
              ) : (
                  <TextSignature onTextChange={setTextSignature} initialText=""/>
              )}

              {(error || localError) && (
                  <div className="alert alert-danger mt-3">{error || localError}</div>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={isLoading}>
                Cancel
              </button>
              <button type="button" className="btn btn-primary" onClick={handleSave}
                  // disabled={
                  //     (activeTab === 'draw' && sigCanvas.current?.isEmpty()) ||
                  //     (activeTab === 'upload' && !uploadedImage) ||
                  //     (activeTab === 'text' && !textSignature)
                  // }
              >
                {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Saving...
                    </>
                ) : (
                    'Save'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
  );
};

export default SignatureModal;
